import type {Mapper} from '@automapper/core';
import type {DownloadColumn, IDownloadByLinkService} from '@igp/shared/api';
import {ServerResponseStatus} from '@igp/shared/api';
import {map} from '@igp/shared/otel';
import {
    type DownloadStrategyRequest,
    type DownloadStrategyResponse,
    EntityDownloadClientStrategy,
    type IDownloadStrategy,
} from '@igp/shared/redux/download';
import {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {
    AgentPlayerReportingV2TypeName,
    PlayerLevelReportV2DownloadFieldsTypeName,
    PlayerLevelReportV2DownloadModelTypeName,
    PlayerLevelReportV2QueryItemsKeysTypeName,
    PlayerLevelReportV2QuerySummaryKeysTypeName,
    PlayerLevelReportV2ViewModelKeysTypeName,
} from './constants';
import type {PlayerLevelReportV2Service} from './service';
import {
    type PlayerLevelReportV2DownloadFields,
    type PlayerLevelReportV2ViewModelKeys,
    ServiceTypes as PlayerLevelReportV2ServiceTypes,
} from './types';

@injectable()
export class PlayerLevelReportV2DownloadClientStrategy extends EntityDownloadClientStrategy<PlayerLevelReportV2ViewModelKeys, EntityType> {
    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(PlayerLevelReportV2ServiceTypes.PlayerLevelReportV2Service) service: PlayerLevelReportV2Service
    ) {
        super(mapper, service, EntityType.PlayerLevelReportV2, {
            viewModelKeys: PlayerLevelReportV2ViewModelKeysTypeName,
            queryFields: PlayerLevelReportV2QueryItemsKeysTypeName,
            summaryQueryFields: PlayerLevelReportV2QuerySummaryKeysTypeName,
            model: AgentPlayerReportingV2TypeName,
            downloadModel: PlayerLevelReportV2DownloadModelTypeName,
        });
    }
}

@injectable()
export class PlayerLevelReportV2DownloadServerStrategy implements IDownloadStrategy<PlayerLevelReportV2ViewModelKeys> {
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<PlayerLevelReportV2DownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(PlayerLevelReportV2ServiceTypes.PlayerLevelReportV2Service)
        service: IDownloadByLinkService<PlayerLevelReportV2DownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(
        request: DownloadStrategyRequest<PlayerLevelReportV2ViewModelKeys>
    ): Observable<DownloadStrategyResponse<PlayerLevelReportV2ViewModelKeys>> {
        const columns: DownloadColumn<PlayerLevelReportV2DownloadFields>[] = request.keys?.map<
            DownloadColumn<PlayerLevelReportV2DownloadFields>
        >(k => {
            const queryField = this._mapper.map<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2DownloadFields>(
                k,
                PlayerLevelReportV2ViewModelKeysTypeName,
                PlayerLevelReportV2DownloadFieldsTypeName
            );
            return {
                name: request.headers[k] ?? k,
                field: queryField,
            };
        });
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}
